import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { BaseModalComponent } from '@card-software/library';
import { ApiResponse } from '@card-software/nexus-utilities';
import { FabricCategory } from '../../../models/fabric/fabricCategory';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FabricCategoryProxyService } from '../../../proxies/item/fabric-category.proxy.service';
import { catchError, concatMap, from, Observable, of, toArray } from 'rxjs';
import { AssetTypeProxyService } from '../../../proxies/asset/asset-type.proxy.service';
import { AssetType } from '../../../models/asset/assetType';
import { CompositionProxyService } from '../../../proxies/item/composition-proxy.service';
import { Composition } from '../../../models/composition/composition';
import { ItemCategoryProxyService } from '../../../proxies/item/item-category.proxy.service';
import { ItemCategory } from '../../../models/item-master';
import { Translation } from '../../../models/Translations';
import { TranslateService } from '@ngx-translate/core';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

type SuccessResponse = ApiResponse<FabricCategory | Composition | ItemCategory>;

@Component({
  selector: 'app-categories-modal',
  templateUrl: './categories-modal.component.html',
  styleUrl: './categories-modal.component.scss',
})
export class CategoriesModalComponent
  extends BaseModalComponent<FabricCategory | FabricCategory[] | Composition | Composition[] | ItemCategory | ItemCategory[] | null>
  implements OnInit
{
  private readonly fabricCategoryProxyService = inject(FabricCategoryProxyService);
  private readonly compositionProxyService = inject(CompositionProxyService);
  private readonly itemCategoryProxyService = inject(ItemCategoryProxyService);
  private readonly assetTypeProxyService = inject(AssetTypeProxyService);
  private readonly translateService = inject(TranslateService);

  @Input() canEdit = false;
  @Input() categoryType: 'Fabric' | 'Fiber' | 'Item' = 'Fabric';
  @Input() category: FabricCategory | Composition | ItemCategory | null = null;
  @Input() createCategory = false;
  @Input() addSubcategories = false;

  machineTypes: AssetType[] = [];

  categoryFormGroup: FormGroup = new FormGroup({
    code: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(3)]),
    name: new FormGroup({
      en: new FormControl('', Validators.required),
      es: new FormControl(''),
    }),
    machineTypeReference: new FormControl(''),
    description: new FormControl(''),
    subcategories: new FormArray([]),
  });

  submittedForm = false;
  isLoading = false;

  spanishTranslate: FormControl<boolean> = new FormControl<boolean>(false, {
    nonNullable: true,
  });

  constructor(injector: Injector) {
    super(injector);
    // Force uppercase value on code input.
    this.categoryFormGroup.get('code')?.valueChanges.subscribe((value) => {
      if (value !== value?.toUpperCase()) {
        this.categoryFormGroup.get('code')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    if (this.categoryType === 'Fabric') {
      this.getMachineTypes();
      this.categoryFormGroup.get('machineTypeReference')?.setValidators(Validators.required);
    }
    // Item category code should be atomatic.
    if (this.categoryType === 'Item') {
      this.categoryFormGroup.get('code')?.clearValidators();
      this.categoryFormGroup.get('code')?.updateValueAndValidity();
    }

    if (this.category?.id) {
      this.patchCategoryFormGroup();
    }

    //Disable form by Rol
    if (!this.canEdit) {
      this.categoryFormGroup.disable();
    }
  }

  getMachineTypes() {
    this.assetTypeProxyService.getByCategoryId('673c07b2a128eab872eb6c2d').subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.machineTypes = response.data;
        }
      },
      error: (error) => {
        console.log('🚀 ~ this.assetTypeProxyService.getByCategoryId ~ error:', error);
      },
    });
  }

  private clearMissingTranslations(field: 'name'): void {
    const langs: (keyof Translation)[] = ['es', 'en'];

    const translation = this.category?.[field];
    if (!translation) return;

    langs.forEach((lang) => {
      if (translation.missingTranslations?.includes(lang)) {
        translation[lang] = '';
      }
    });
  }

  private syncSpanishTranslateToggle(): void {
    const name = this.category?.name;
    const hasSpanishTranslation = !!name?.es?.trim() && !name.missingTranslations?.includes('es');

    this.spanishTranslate.setValue(hasSpanishTranslation);
  }

  patchCategoryFormGroup() {
    //clearMissingTranslations values
    this.clearMissingTranslations('name');
    this.syncSpanishTranslateToggle();

    this.categoryFormGroup.patchValue({
      code: this.category?.code ?? null,
      name: this.category?.name ?? null,
      description: (this.category as ItemCategory).description ?? null,
      machineTypeReference: (this.category as FabricCategory).machineTypeReference ?? null,
    });
    // Disable category fields
    if (this.addSubcategories) {
      this.categoryFormGroup.controls['name'].disable();
      this.categoryFormGroup.controls['description'].disable();
      this.addSubcategory();
    }

    if (this.category?.parentCode) {
      this.categoryFormGroup.controls['machineTypeReference'].disable();
    }

    this.categoryFormGroup.controls['code'].disable();
  }

  get subcategories(): FormArray {
    return this.categoryFormGroup.get('subcategories') as FormArray;
  }

  addSubcategory() {
    let subcategoryGroup: FormGroup;

    if (this.categoryType === 'Fabric') {
      subcategoryGroup = new FormGroup({
        name: new FormGroup({
          en: new FormControl('', Validators.required),
          es: new FormControl(''),
        }),
      });
    } else if (this.categoryType === 'Fiber') {
      subcategoryGroup = new FormGroup({
        name: new FormGroup({
          en: new FormControl('', Validators.required),
          es: new FormControl(''),
        }),
        code: new FormControl('', Validators.required),
      });
    } else {
      subcategoryGroup = new FormGroup({
        name: new FormGroup({
          en: new FormControl('', Validators.required),
          es: new FormControl(''),
        }),
        description: new FormControl(''),
      });
    }

    this.subcategories.push(subcategoryGroup);
  }

  removeSubcategory(index: number) {
    this.subcategories.removeAt(index);
  }

  onSave() {
    this.submittedForm = true;
    if (this.categoryFormGroup.valid) {
      const formValue = this.categoryFormGroup.getRawValue();
      const category = {
        ...(this.createCategory ? {} : this.category),
        id: this.createCategory ? undefined : this.category?.id,
        parentCode: this.category?.parentCode,
        code: formValue.code?.toUpperCase(),
        name: formValue.name,
      } as FabricCategory | Composition | ItemCategory;

      (category as ItemCategory).description = formValue.description;
      (category as FabricCategory).machineTypeReference = formValue.machineTypeReference;
      delete (category as FabricCategory).subcategories;

      if (this.categoryType === 'Fabric') {
        const fabricCategory = category as FabricCategory;
        fabricCategory.id = this.createCategory ? '' : (fabricCategory.id ?? '');
        fabricCategory.code = fabricCategory.code ?? '';
        fabricCategory.name = {
          en: fabricCategory.name?.en ?? '',
          es: fabricCategory.name?.es ?? '',
        };
        fabricCategory.displayName = fabricCategory.displayName ?? fabricCategory.name;
        fabricCategory.level = fabricCategory.level ?? (fabricCategory.parentCode ? 2 : 1);
        fabricCategory.selectable = fabricCategory.selectable ?? true;
        fabricCategory.isActive = fabricCategory.isActive ?? true;
      }

      if (this.categoryType === 'Fiber') {
        const fiberCategory = category as Composition;
        fiberCategory.id = this.createCategory ? '' : (fiberCategory.id ?? '');
      }

      if (this.addSubcategories) {
        if (this.category && this.subcategories.value.length > 0) {
          this.saveSubCategories();
        }
      } else {
        this.saveSingleCategory(category);
      }
    }
  }

  saveSubCategories() {
    this.isLoading = true;
    const subcategories: {
      name: Translation;
      code?: string;
      description?: string;
    }[] = this.subcategories.value;

    from(subcategories)
      .pipe(
        concatMap((subcategory) => {
          const parentCategory = this.category!;

          if (this.categoryType === 'Fabric') {
            const category: FabricCategory = {
              id: '',
              code: '',
              parentCode: parentCategory.code,
              name: {
                en: subcategory.name.en,
                es: subcategory.name.es,
              },
              displayName: {
                en: subcategory.name.en,
                es: subcategory.name.es,
              },
              level: (parentCategory.level ?? 1) + 1,
              selectable: true,
              machineTypeReference: (parentCategory as FabricCategory).machineTypeReference,
              isActive: true,
            };

            return this.fabricCategoryProxyService.createFabricCategory(category).pipe(catchError(() => of(null)));
          } else if (this.categoryType === 'Fiber') {
            const category: Composition = {
              id: '',
              parentCode: parentCategory.code,
              name: subcategory.name,
              code: `${parentCategory.code}-${subcategory.code?.toUpperCase()}`,
            };

            return this.compositionProxyService.createFiberCategory(category).pipe(catchError(() => of(null)));
          } else {
            const category = {
              parentCode: parentCategory.code ?? '',
              name: subcategory.name,
              level: parentCategory.level! + 1,
              description: subcategory.description,
              isActive: true,
            } as ItemCategory;

            return this.itemCategoryProxyService.createItemCategory(category).pipe(catchError(() => of(null)));
          }
        }),
        toArray(), // saved subcategories array
      )
      .subscribe((results) => {
        this.isLoading = false;
        const success = results.filter((r): r is SuccessResponse => r !== null);
        const subcategories: FabricCategory[] = success.map((r) => r.data).filter((data): data is FabricCategory => data !== null);

        if (success.length) {
          GlobalMessageUtil.success(
            this.translateService.instant('Common_Success'),
            `${success.length} ${this.translateService.instant('Categories_Modal_SubCategoriesSaved_Msg')}`,
          );
        }

        this.close({ action: 'save_subcategories', data: subcategories });
      });
  }

  saveSingleCategory(category: FabricCategory | Composition | ItemCategory) {
    this.isLoading = true;
    let save$: Observable<ApiResponse<FabricCategory | Composition | ItemCategory>>;

    if (this.createCategory) {
      if (this.categoryType === 'Fabric') {
        save$ = this.fabricCategoryProxyService.createFabricCategory(category as FabricCategory);
      } else if (this.categoryType === 'Fiber') {
        save$ = this.compositionProxyService.createFiberCategory(category as Composition);
      } else {
        category = {
          ...category,
          level: 1,
          isActive: true,
        } as ItemCategory;
        save$ = this.itemCategoryProxyService.createItemCategory(category as ItemCategory);
      }
      category.id = '';
    } else {
      const categoryId = this.category?.id ?? '';

      if (this.categoryType === 'Fabric') {
        save$ = this.fabricCategoryProxyService.updateFabricCategory(categoryId, category as FabricCategory);
      } else if (this.categoryType === 'Fiber') {
        save$ = this.compositionProxyService.updateFiberCategory(categoryId, category as Composition);
      } else {
        save$ = this.itemCategoryProxyService.updateItemCategory(categoryId, category as ItemCategory);
      }
    }

    save$.subscribe({
      next: (response) => {
        this.isLoading = false;
        this.close({
          action: 'save',
          data: this.createCategory ? response.data : { id: this.category?.id, ...category },
        });

        GlobalMessageUtil.success(
          this.translateService.instant('Common_Success'),
          this.createCategory
            ? this.translateService.instant('Categories_Modal_Category_Saved', { name: response.data?.code ?? '' })
            : this.translateService.instant('Categories_Modal_Category_Updated', { name: category.code ?? '' }),
        );
      },
      error: (error) => {
        this.isLoading = false;
        console.error('❌ Error saving category:', error);
      },
    });
  }

  onClose() {
    this.close({ action: 'close', data: null });
  }
}
